import { deserializeMergeFieldTokens, type IMergeFieldToken, serializeMergeFieldTokens } from '@levitate/rich-text';
import type * as Api from '@ViewModels';
import { contentHasFileLinkToken } from '../../../models/utils/richContentTokenUtils';
import {
	BioPlaceholder,
	CityPlaceholder,
	CompanyNamePlaceholder,
	FileRequestLinkPlaceholder,
	FileSendLinkPlaceholder,
	FirstNamePlaceholder,
	FullNamePlaceholder,
	JobTitlePlaceholder,
	LastNamePlaceholder,
	MeetingHomepageLinkPlaceholder,
	SenderEmailPlaceholder,
	SenderFirstNamePlaceholder,
	SenderFullNamePlaceholder,
	SenderPhonePlaceholder,
	StatePlaceholder,
} from '../../../models/Token';

type Placeholder = { symbol: string; text: string; value?: string; additionalSymbols?: string[] };

/** Every literal symbol (primary + aliases) that resolves to the secure file-send download link. */
export const FILE_SEND_SYMBOLS = [FileSendLinkPlaceholder.symbol, ...(FileSendLinkPlaceholder.additionalSymbols ?? [])];

/** The `data-placeholder-value` a legacy TinyMCE file-send chip carries. */
export const FILE_SEND_PLACEHOLDER_VALUE = FileSendLinkPlaceholder.value;

/**
 * Contact/sender/meeting fields the backend resolves as `data-placeholder` spans. Kept as span-mode
 * tokens (value present) so they render as pills and round-trip to the legacy placeholder contract.
 */
const SPAN_MODE_PLACEHOLDERS: Placeholder[] = [
	FirstNamePlaceholder,
	LastNamePlaceholder,
	FullNamePlaceholder,
	CompanyNamePlaceholder,
	JobTitlePlaceholder,
	CityPlaceholder,
	StatePlaceholder,
	BioPlaceholder,
	SenderFirstNamePlaceholder,
	SenderFullNamePlaceholder,
	SenderEmailPlaceholder,
	SenderPhonePlaceholder,
	MeetingHomepageLinkPlaceholder,
];

const symbolsOf = (placeholder: Placeholder) => [placeholder.symbol, ...(placeholder.additionalSymbols ?? [])];

const spanModeTokens = (placeholder: Placeholder): IMergeFieldToken[] =>
	symbolsOf(placeholder).map(symbol => ({ symbol, text: placeholder.text, value: placeholder.value }));

/**
 * File send/request resolve to a link the backend builds from the bare `{{...}}` text, so they are
 * raw-mode tokens (no `value`): they serialize back to the literal token, and removal matches by symbol.
 */
const rawModeTokens = (placeholder: Placeholder): IMergeFieldToken[] =>
	symbolsOf(placeholder).map(symbol => ({ symbol, text: placeholder.text }));

export function buildCampaignComposerTokens({
	canSendFileLinks,
	canRequestFiles,
}: {
	canSendFileLinks?: boolean;
	canRequestFiles?: boolean;
}): IMergeFieldToken[] {
	const tokens = SPAN_MODE_PLACEHOLDERS.flatMap(spanModeTokens);
	if (canRequestFiles) {
		tokens.push(...rawModeTokens(FileRequestLinkPlaceholder));
	}
	if (canSendFileLinks) {
		tokens.push(...rawModeTokens(FileSendLinkPlaceholder));
	}
	return tokens;
}

export function getFileSendBlockingError({
	content,
	hasAttachments,
	sendAttachmentsAsLink,
}: {
	content: Api.IRawRichTextContentState | undefined;
	hasAttachments: boolean;
	sendAttachmentsAsLink?: boolean;
}): string | null {
	const hasToken = contentHasFileLinkToken(content);
	if (!hasToken) {
		// Toggle on with attachments but no token is reachable by deleting the merge field text without
		// touching the toggle, which the UI otherwise only flags with a non-blocking warning.
		if (sendAttachmentsAsLink && hasAttachments) {
			return 'Your message needs a "Securely Download Files" merge field to send your files as a download link. Add the merge field or turn off "Send as download link".';
		}
		return null;
	}
	if (!hasAttachments) {
		return 'Your message includes a "Securely Download Files" merge field, but no files are attached. Attach a file or remove the merge field.';
	}
	if (!sendAttachmentsAsLink) {
		return 'Your message includes a "Securely Download Files" merge field, but "Send as download link" is turned off. Turn it on or remove the merge field.';
	}
	return null;
}

/** Stored document HTML -> editor HTML: wrap known raw `{{...}}` tokens as pill markers. */
export function storedHtmlToEditorHtml(storedHtml: string, tokens: IMergeFieldToken[]): string {
	return deserializeMergeFieldTokens(storedHtml, tokens);
}

/** Editor HTML -> stored document HTML: unwrap raw-mode markers back to literal `{{...}}`. */
export function editorHtmlToStoredHtml(editorHtml: string): string {
	return serializeMergeFieldTokens(editorHtml);
}
